import moment from 'moment';

import {
  calculateAge,
  filestackPath,
  formatDate,
  ia,
  kebabToReadable,
  safeParseJSON
} from '../utility';

import { GAP, PAGE_COLOR } from './lib/constants';

const getImageFileId = (image) => {
  const procedureImage = safeParseJSON(image, image);
  const finalImageId =
    typeof procedureImage === 'string' && !procedureImage.includes('{')
      ? procedureImage
      : procedureImage?.jpg || procedureImage?.png;

  return finalImageId;
};

const generateHeaderImage = (doc, image) => {
  const finalImageId = getImageFileId(image);

  if (!finalImageId) {
    return;
  }

  const img = filestackPath(finalImageId);

  const maxWidth = 50;
  const maxHeight = 30;

  //This fixes image to contain the max width and height
  try {
    const { width: imgWidth, height: imgHeight } = doc.getImageProperties(img);

    const widthRatio = maxWidth / imgWidth;
    const heightRatio = maxHeight / imgHeight;
    const scale = Math.min(widthRatio, heightRatio);

    const finalWidth = imgWidth * scale;
    const finalHeight = imgHeight * scale;

    //This centers the image inside the box
    const xPos = GAP + (maxWidth - finalWidth) / 2;
    const yPos = GAP + (maxHeight - finalHeight) / 2;

    //This adds the image to the document
    doc.addImage(img, 'JPEG', xPos, yPos, finalWidth, finalHeight);
  } catch (error) {
    console.error('Error loading image:', error);
  }
};

export const generateHeader = ({ doc, startY = GAP, headerConfigs }) => {
  let finalStartY = startY;

  const { practice, patient, hiddenFields } = headerConfigs || {};

  const pageWidth = doc.internal.pageSize.width;

  if (practice?.header_photo) {
    generateHeaderImage(doc, practice.header_photo);
  }

  finalStartY += 5;

  if (practice) {
    const practiceDetails = [
      practice.displayName || '',
      getAddressDisplay(practice?.address) || '',
      practice.phone || '',
      practice.email || '',
      practice.tax_id || ''
    ];
    doc.setFontSize(10);

    let practiceTextY = 15;
    const practiceTextX = pageWidth - GAP;

    practiceDetails.forEach((text) => {
      const textWidth = doc.getTextWidth(text);
      doc.text(text, practiceTextX - textWidth, practiceTextY);
      practiceTextY += 5;
    });

    finalStartY += 30;
  }

  if (patient) {
    const hideName = hiddenFields?.patient?.includes('pt_name');
    const hidePtDob = hiddenFields?.patient?.includes('pt_dob');
    const hidePtAge = hiddenFields?.patient?.includes('pt_age');
    const hidePtGender = hiddenFields?.patient?.includes('pt_gender');
    const hidePtAddress = hiddenFields?.patient?.includes('pt_address');

    doc.setFillColor(PAGE_COLOR);
    doc.rect(0, finalStartY, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    let lineY = finalStartY + 10;
    if (!hideName && !!patient?.fullName) {
      doc.text(patient.fullName, GAP, lineY);
      lineY += 5;
    }

    const detailsParts = [];
    if (!hidePtDob && patient?.dob) {
      detailsParts.push(`${moment(patient?.dob).format('MM/DD/YYYY')}`);
    }
    if (!hidePtAge && patient?.dob) {
      detailsParts.push(`${calculateAge({ dateOfBirth: patient.dob })} years`);
    }
    if (!hidePtGender && patient?.gender) {
      detailsParts.push(kebabToReadable(patient.gender));
    }

    if (detailsParts.length) {
      doc.setFontSize(10);
      doc.text(detailsParts.join(' • '), GAP, lineY);
      lineY += 5;
    }

    doc.setFontSize(10);

    if (!hidePtAddress && !!patient?.userAddress) {
      doc.setFontSize(10);
      doc.text(getAddressDisplay(patient.userAddress), GAP, lineY);
      lineY += 5;
    }

    const dateServiceText = `Date of Service: ${formatDate(patient?.service_date, practice?.timezone)}`;
    const chartIDText = `Chart ID: ${patient?.id}`;

    const dateOfServiceX = pageWidth - GAP;

    if (!!patient.service_date) {
      doc.text(
        dateServiceText,
        dateOfServiceX - doc.getTextWidth(dateServiceText),
        finalStartY + 10
      );
    }

    if (!!patient?.id) {
      doc.text(chartIDText, dateOfServiceX - doc.getTextWidth(chartIDText), finalStartY + 15);
    }

    finalStartY += 30;
  }

  return finalStartY;
};

function getAddressDisplay(address) {
  if (address && typeof address === 'object' && !ia(address)) {
    return address?.fullAddress;
  }

  if (address && typeof address !== 'number') {
    return address;
  }

  return null;
}
